import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Shield, Clock, Phone, Mail, User, MapPin, 
  Plus, Trash2, AlertTriangle, CheckCircle2, Timer, Heart,
  Calendar, Syringe, ExternalLink, Bell, BellOff
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Safety = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [activeCheckIn, setActiveCheckIn] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showStartCheckIn, setShowStartCheckIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // New contact form
  const [newContact, setNewContact] = useState({ name: '', phone: '', email: '' });
  
  // Check-in form
  const [checkInData, setCheckInData] = useState({
    meeting_with_user_id: '',
    duration_minutes: 60,
    location_name: '',
    notes: ''
  });
  
  // Recent conversations for meeting selection
  const [recentUsers, setRecentUsers] = useState([]);
  
  // STI Reminder settings
  const [stiReminderDays, setStiReminderDays] = useState(user?.sti_reminder_days || null);

  const fetchData = useCallback(async () => {
    try {
      const [contactsRes, checkInRes, conversationsRes] = await Promise.all([
        axios.get(`${API}/users/emergency-contacts`),
        axios.get(`${API}/safety/check-in/active`),
        axios.get(`${API}/messages/conversations`)
      ]);
      
      setEmergencyContacts(contactsRes.data);
      setActiveCheckIn(checkInRes.data);
      
      // Extract users from conversations
      const users = conversationsRes.data
        .filter(c => c.other_user)
        .map(c => c.other_user);
      setRecentUsers(users);
    } catch (error) {
      console.error('Failed to fetch safety data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Timer for active check-in
  useEffect(() => {
    if (!activeCheckIn) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const expiresAt = new Date(activeCheckIn.expires_at);
      const now = new Date();
      const diff = expiresAt - now;
      
      if (diff <= 0) {
        setTimeRemaining(0);
        // Check-in expired - would trigger alert in production
        toast.error('Check-in expired! Your emergency contacts would be notified.');
      } else {
        setTimeRemaining(Math.floor(diff / 1000));
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeCheckIn]);

  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone) {
      toast.error('Name and phone are required');
      return;
    }
    
    try {
      await axios.post(`${API}/users/emergency-contacts`, newContact);
      toast.success('Emergency contact added');
      setShowAddContact(false);
      setNewContact({ name: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/users/emergency-contacts/${contactId}`);
      toast.success('Contact removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  const handleStartCheckIn = async () => {
    if (!checkInData.meeting_with_user_id) {
      toast.error('Please select who you are meeting');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/safety/check-in`, checkInData);
      setActiveCheckIn(response.data.check_in);
      setShowStartCheckIn(false);
      toast.success('Safety check-in started!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start check-in');
    }
  };

  const handleConfirmSafe = async () => {
    try {
      await axios.post(`${API}/safety/check-in/${activeCheckIn.id}/confirm`);
      setActiveCheckIn(null);
      toast.success('Glad you are safe! Check-in complete.');
    } catch (error) {
      toast.error('Failed to confirm check-in');
    }
  };

  const handleExtendCheckIn = async () => {
    try {
      const response = await axios.post(`${API}/safety/check-in/${activeCheckIn.id}/extend?minutes=30`);
      setActiveCheckIn(prev => ({ ...prev, expires_at: response.data.new_expires_at }));
      toast.success('Extended by 30 minutes');
    } catch (error) {
      toast.error('Failed to extend check-in');
    }
  };

  const handleCancelCheckIn = async () => {
    try {
      await axios.post(`${API}/safety/check-in/${activeCheckIn.id}/cancel`);
      setActiveCheckIn(null);
      toast.success('Check-in cancelled');
    } catch (error) {
      toast.error('Failed to cancel check-in');
    }
  };

  const handleUpdateSTIReminder = async (days) => {
    try {
      await axios.put(`${API}/users/sti-reminder`, { reminder_days: days });
      setStiReminderDays(days);
      await refreshUser();
      toast.success(days ? `Reminder set for every ${days} days` : 'Reminder disabled');
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const handleMarkTested = async () => {
    try {
      await axios.post(`${API}/users/sti-reminder/mark-tested`);
      await refreshUser();
      toast.success('Great job staying healthy! 💪');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#08D9D6]" />
            <h1 className="text-lg font-bold text-white">Safety Center</h1>
          </div>
        </div>
      </header>

      <div className="px-4 py-6 space-y-6">
        {/* Active Check-In Banner */}
        {activeCheckIn && (
          <div className="glass rounded-xl p-4 border-2 border-[#FFD700]/50 bg-[#FFD700]/10" data-testid="active-checkin">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Timer className="w-5 h-5 text-[#FFD700]" />
                <span className="text-[#FFD700] font-semibold">Safety Check-In Active</span>
              </div>
              <div className={`text-2xl font-mono font-bold ${
                timeRemaining && timeRemaining < 300 ? 'text-red-500' : 'text-[#FFD700]'
              }`}>
                {formatTime(timeRemaining)}
              </div>
            </div>
            
            <p className="text-sm text-[#A1A1AA] mb-4">
              Meeting with <span className="text-white font-medium">{activeCheckIn.meeting_with_username}</span>
              {activeCheckIn.location_name && ` at ${activeCheckIn.location_name}`}
            </p>
            
            <div className="grid grid-cols-3 gap-2">
              <Button
                data-testid="confirm-safe-btn"
                onClick={handleConfirmSafe}
                className="bg-[#22c55e] hover:bg-[#16a34a] text-white"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Safe
              </Button>
              <Button
                data-testid="extend-btn"
                onClick={handleExtendCheckIn}
                variant="outline"
                className="border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10"
              >
                +30 min
              </Button>
              <Button
                data-testid="cancel-checkin-btn"
                onClick={handleCancelCheckIn}
                variant="ghost"
                className="text-[#A1A1AA] hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Start Check-In */}
        {!activeCheckIn && (
          <div className="glass rounded-xl p-4">
            <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#FF2E63]" />
              Safety Check-In
            </h2>
            <p className="text-sm text-[#A1A1AA] mb-4">
              Set a timer when meeting someone. If you do not check in, your emergency contacts will be notified.
            </p>
            <Button
              data-testid="start-checkin-btn"
              onClick={() => setShowStartCheckIn(true)}
              className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
              disabled={emergencyContacts.length === 0}
            >
              <Timer className="w-4 h-4 mr-2" />
              Start Check-In
            </Button>
            {emergencyContacts.length === 0 && (
              <p className="text-xs text-[#FFD700] mt-2 text-center">
                Add an emergency contact first
              </p>
            )}
          </div>
        )}

        {/* Emergency Contacts */}
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#08D9D6]" />
              Emergency Contacts
            </h2>
            {emergencyContacts.length < 3 && (
              <Button
                data-testid="add-contact-btn"
                size="sm"
                onClick={() => setShowAddContact(true)}
                className="bg-[#08D9D6] hover:bg-[#08D9D6]/80 text-black"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            )}
          </div>
          
          {emergencyContacts.length === 0 ? (
            <div className="text-center py-6">
              <User className="w-12 h-12 text-[#52525B] mx-auto mb-2" />
              <p className="text-[#A1A1AA] text-sm">No emergency contacts yet</p>
              <p className="text-xs text-[#52525B]">Add someone who can be contacted if needed</p>
            </div>
          ) : (
            <div className="space-y-3">
              {emergencyContacts.map(contact => (
                <div key={contact.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{contact.name}</p>
                    <p className="text-sm text-[#A1A1AA]">{contact.phone}</p>
                    {contact.email && (
                      <p className="text-xs text-[#52525B]">{contact.email}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteContact(contact.id)}
                    className="text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STI Testing Reminders */}
        <div className="glass rounded-xl p-4">
          <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <Syringe className="w-5 h-5 text-[#FF2E63]" />
            STI Testing Reminders
          </h2>
          <p className="text-sm text-[#A1A1AA] mb-4">
            Regular testing is important for your health and your partners.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Label className="text-[#EDEDED] flex-1">Remind me every:</Label>
              <Select
                value={stiReminderDays?.toString() || 'none'}
                onValueChange={(v) => handleUpdateSTIReminder(v === 'none' ? null : parseInt(v))}
              >
                <SelectTrigger data-testid="sti-reminder-select" className="w-32 bg-white/5 border-white/10">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="none">Off</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {user?.sti_last_tested_date && (
              <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                <Calendar className="w-4 h-4" />
                Last tested: {new Date(user.sti_last_tested_date).toLocaleDateString()}
              </div>
            )}
            
            <Button
              data-testid="mark-tested-btn"
              onClick={handleMarkTested}
              variant="outline"
              className="w-full border-[#22c55e]/50 text-[#22c55e] hover:bg-[#22c55e]/10"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              I Got Tested Today
            </Button>
            
            <a
              href="https://gettested.cdc.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-sm text-[#08D9D6] hover:underline"
            >
              Find testing locations near you
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Safety Tips */}
        <div className="glass rounded-xl p-4">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#FF2E63]" />
            Safety Tips
          </h2>
          <ul className="space-y-3 text-sm text-[#A1A1AA]">
            <li className="flex items-start gap-2">
              <span className="text-[#08D9D6]">•</span>
              Always meet in a public place first
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#08D9D6]">•</span>
              Tell a friend where you are going
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#08D9D6]">•</span>
              Trust your instincts - if something feels off, leave
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#08D9D6]">•</span>
              Keep your phone charged
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#08D9D6]">•</span>
              Have your own transportation
            </li>
          </ul>
        </div>
      </div>

      {/* Add Contact Modal */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Add Emergency Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Name *</Label>
              <Input
                data-testid="contact-name-input"
                value={newContact.name}
                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Contact name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Phone *</Label>
              <Input
                data-testid="contact-phone-input"
                value={newContact.phone}
                onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                placeholder="+1 234 567 8900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Email (optional)</Label>
              <Input
                data-testid="contact-email-input"
                value={newContact.email}
                onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                placeholder="email@example.com"
              />
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowAddContact(false)}
              className="text-[#A1A1AA] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              data-testid="save-contact-btn"
              onClick={handleAddContact}
              className="bg-[#08D9D6] hover:bg-[#08D9D6]/80 text-black"
            >
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Check-In Modal */}
      <Dialog open={showStartCheckIn} onOpenChange={setShowStartCheckIn}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Start Safety Check-In</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Meeting with *</Label>
              <Select
                value={checkInData.meeting_with_user_id}
                onValueChange={(v) => setCheckInData(prev => ({ ...prev, meeting_with_user_id: v }))}
              >
                <SelectTrigger data-testid="meeting-user-select" className="bg-white/5 border-white/10">
                  <SelectValue placeholder="Select person" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                  {recentUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.display_name || u.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Check-in after *</Label>
              <Select
                value={checkInData.duration_minutes.toString()}
                onValueChange={(v) => setCheckInData(prev => ({ ...prev, duration_minutes: parseInt(v) }))}
              >
                <SelectTrigger data-testid="duration-select" className="bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Location (optional)</Label>
              <Input
                data-testid="location-input"
                value={checkInData.location_name}
                onChange={(e) => setCheckInData(prev => ({ ...prev, location_name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Coffee shop, their place, etc."
              />
            </div>
            <div className="p-3 bg-[#FFD700]/10 rounded-lg">
              <p className="text-xs text-[#FFD700]">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                If you do not check in within the time, your emergency contacts will receive your last known location.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowStartCheckIn(false)}
              className="text-[#A1A1AA] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-start-btn"
              onClick={handleStartCheckIn}
              className="bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              Start Check-In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default Safety;
